import type { ComputedRef, Ref } from "vue";
import type {
  FileValidationErrorType,
  MultiFileUploaderBatchErrorCode,
} from "@plugins/file-uploader-v2/file.uploader.constant.ts";

export interface FileResponse {
  status?: FileStatusValues;
  uuid?: string;
  name?: string;
  url?: string;
  errorType?: FileValidationErrorType;
  errorTitle?: string;
  errorMessage?: string[];
}

export interface FileEntry {
  fileUuid: string;
  filename: string;
  fileVersion?: string;
}

export interface FileUploadEntry {
  state: "scanning" | "completed" | "failed" | "error" | string;
  file: File;
  originalFileName: string;
  invalidMessageTitle: string;
  invalidMessage: string;
  invalidMessageType?: MultiFileUploaderErrorType;
  isValid: undefined | boolean;
  uuid: string;
  compressed: true | false;
}

export type MultiFileUploaderErrorType = "file-type" | "file-size" | "upload";

export interface MultiFileUploaderBatchError {
  code: MultiFileUploaderBatchErrorCode;
  limit: number;
  actual: number;
}

export interface MultiFileUploaderBatchValidationOptions {
  maxFileCount?: number;
  maxTotalFileSize?: number;
}

export interface MultiFileUploaderState {
  isProcessing: boolean;
  isUploading: boolean;
  canUpload: boolean;
  hasFiles: boolean;
}

export interface MultiFileUploaderFileEntryView {
  entry: FileUploadEntry;
  key: string;
  isLoading: boolean;
  kind: "processing" | undefined;
  mediaSize: string;
  iconType: string;
  canRemove: boolean;
}

export type MultiFileUploaderLinkedFileErrorMessageKey =
  "multi_file_upload_invalid_file_type_error" | "multi_file_upload_file_size_error";

export interface MultiFileUploaderLinkedFileErrorView {
  entry: FileUploadEntry;
  messageKey: MultiFileUploaderLinkedFileErrorMessageKey | undefined;
}

export interface MultiFileUploaderModalParams {
  modalId: string;
  uploadFlow: UploadFlowValues;
  minimumLoadingDurationMs?: number;
  recordType?: string;
  recordUuid?: string;
}

export interface MultiFileUploaderOptions extends MultiFileUploaderBatchValidationOptions {
  recordType?: Ref<string | undefined>;
  recordUuid?: Ref<string | undefined>;
}

export interface UseMultiFileUploaderResult {
  fileEntries: Ref<FileUploadEntry[]>;
  batchErrors: ComputedRef<MultiFileUploaderBatchError[]>;
  totalFileSize: ComputedRef<number>;
  isBatchValid: ComputedRef<boolean>;
  maxFileCount: number;
  validateFiles: (files: File[]) => Promise<void>;
  uploadFiles: () => Promise<void>;
  removeFile: (entry: FileUploadEntry) => void;
}

export interface FileUploadResult {
  fileUuid: string;
  status: "SCANNING" | "READY" | "FAILED" | string;
  fileName: string;
}

export interface FileUploadRequest {
  location?: string;
  resourceType?: string;
  resourceUuid?: string;
  contentType: string;
  filename: string;
  uploadFlow?: UploadFlowValues;
}

export interface FileUploadModel {
  fileUuid: string;
  signedUploadUrl: string;
}

export const Dimensions = {
  limit: { width: 5000, height: 10000 },
  large: { width: 900, height: 450 },
  medium: { width: 680, height: 454 },
  small: { width: 128, height: 128 },
  achievementIcon: { width: 180, height: 180 },
  gameStoryIcon: { width: 420, height: 180 },
  drawing: { width: 455, height: 455 },
  forumTopic: { width: 120, height: 120 },
  contentMapThumbnail: { width: 622, height: 266 },
};

export const FileStatus = {
  SCANNING: "SCANNING",
  READY: "READY",
  FAILED: "FAILED",
  FILE_MIME_TYPE_NOT_ALLOWED: "FILE_MIME_TYPE_NOT_ALLOWED",
  FILE_SIZE_EXCEED: "FILE_SIZE_EXCEED",
  ERROR: "ERROR",
} as const;
export type FileStatusValues = (typeof FileStatus)[keyof typeof FileStatus];

export const TERMINAL_FILE_STATUSES = new Set<FileStatusValues>([
  FileStatus.FAILED,
  FileStatus.FILE_MIME_TYPE_NOT_ALLOWED,
  FileStatus.FILE_SIZE_EXCEED,
  FileStatus.ERROR,
]);

export const FileType = {
  AUDIO: "AUDIO",
  VIDEO: "VIDEO",
  IMAGE: "IMAGE",
  PDF: "PDF",
  WORD: "WORD",
  EXCEL: "EXCEL",
  POWERPOINT: "POWERPOINT",
  ZIP: "ZIP",
  CSV: "CSV",
  VTT: "VTT",
  ICS: "ICS",
  OTHERS: "OTHERS",
} as const;
export type FileTypeValues = (typeof FileType)[keyof typeof FileType];

export const FileTypeOrder: FileTypeValues[] = [
  FileType.AUDIO,
  FileType.VIDEO,
  FileType.IMAGE,
  FileType.PDF,
  FileType.WORD,
  FileType.EXCEL,
  FileType.POWERPOINT,
  FileType.ZIP,
  FileType.CSV,
  FileType.VTT,
  FileType.ICS,
];

export const UploadFlow = {
  IMG: "IMG",
  AUDIO: "AUDIO",
  AUDIO_AND_VIDEO: "AUDIO_AND_VIDEO",
  CSV: "CSV",
  ZIP: "ZIP",
  VTT: "VTT",
  SPLIT_MEDIA: "SPLIT_MEDIA",
  PDF: "PDF",
  ICS: "ICS",
  ANY_FILE: "ANY_FILE",
} as const;
export type UploadFlowValues = (typeof UploadFlow)[keyof typeof UploadFlow];

export interface FileTypeLimit {
  ext: string[];
  mime: string[];
  size: number;
}

export const FileTypeMap: Record<FileTypeValues, FileTypeLimit> = {
  [FileType.AUDIO]: {
    ext: ["mp3", "mpa", "ogg", "webm", "wav", "wma", "m4a"],
    mime: [
      "audio/mpeg",
      "audio/mp3",
      "audio/ogg",
      "audio/vorbis",
      "audio/webm",
      "application/x-matroska",
      "audio/opus",
      "audio/x-ms-wma",
      "audio/wav",
      "audio/x-wav",
      "audio/vnd.wave",
      "audio/mp4",
      "audio/m4a",
      "audio/x-m4a",
    ],
    size: 52_428_800, // 50 MB
  },

  [FileType.VIDEO]: {
    ext: ["mp4", "mov", "m4v", "ogv", "avi", "mpg", "mpeg", "wmv", "webm"],
    mime: [
      "video/mp4",
      "video/x-m4v",
      "video/ogg",
      "video/theora",
      "video/vnd.avi",
      "video/avi",
      "video/msvideo",
      "video/x-msvideo",
      "video/quicktime",
      "video/mpeg",
      "video/x-ms-wmv",
      "video/webm",
    ],
    size: 524_288_000, // 500 MB
  },

  [FileType.IMAGE]: {
    ext: ["gif", "jpg", "jpeg", "png"],
    mime: ["image/jpeg", "image/gif", "image/png"],
    size: 10_485_760, // 10 MB
  },

  [FileType.PDF]: {
    ext: ["pdf"],
    mime: ["application/pdf"],
    size: 52_428_800, // 50 MB
  },

  [FileType.WORD]: {
    ext: ["docx", "odt", "pages"],
    mime: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.oasis.opendocument.text",
      "application/x-iwork-pages-sffpages",
      "application/vnd.apple.pages",
    ],
    size: 52_428_800,
  },

  [FileType.EXCEL]: {
    ext: ["xlsx", "ods", "numbers"],
    mime: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/x-iwork-numbers-sffnumbers",
      "application/vnd.apple.numbers",
    ],
    size: 52_428_800,
  },

  [FileType.POWERPOINT]: {
    ext: ["pptx", "odp", "odg", "key", "pps"],
    mime: [
      "application/vnd.oasis.opendocument.graphics",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.oasis.opendocument.presentation",
      "application/vnd.ms-powerpoint",
      "application/x-iwork-keynote-sffkey",
      "application/vnd.apple.keynote",
    ],
    size: 52_428_800,
  },

  [FileType.ZIP]: {
    ext: ["zip", "sb2", "sb3", "usdz"],
    mime: ["application/zip", "application/x-zip-compressed", "application/vnd.usdz+zip"],
    size: 524_288_000,
  },

  [FileType.CSV]: {
    ext: ["csv"],
    mime: ["text/csv"],
    size: 524_288_000,
  },

  [FileType.VTT]: {
    ext: ["vtt"],
    mime: ["text/vtt"],
    size: 524_288_000,
  },

  [FileType.ICS]: {
    ext: ["ics"],
    mime: ["text/calendar"],
    size: 5_242_880, // 5 MB
  },

  [FileType.OTHERS]: {
    ext: ["odf", "txt", "skp"],
    mime: ["application/vnd.oasis.opendocument.formula", "text/plain", "application/x-koan"],
    size: 52_428_800,
  },
};

export const UploadFlowFileTypeMap: Record<UploadFlowValues, FileTypeValues[]> = {
  [UploadFlow.AUDIO]: [FileType.AUDIO],
  [UploadFlow.AUDIO_AND_VIDEO]: [FileType.AUDIO, FileType.VIDEO],
  [UploadFlow.IMG]: [FileType.IMAGE],
  [UploadFlow.CSV]: [FileType.CSV],
  [UploadFlow.VTT]: [FileType.VTT],
  [UploadFlow.SPLIT_MEDIA]: [FileType.AUDIO, FileType.VIDEO, FileType.POWERPOINT],
  [UploadFlow.ZIP]: [FileType.ZIP],
  [UploadFlow.PDF]: [FileType.PDF],
  [UploadFlow.ICS]: [FileType.ICS],
  [UploadFlow.ANY_FILE]: [
    FileType.AUDIO,
    FileType.VIDEO,
    FileType.IMAGE,
    FileType.PDF,
    FileType.WORD,
    FileType.EXCEL,
    FileType.POWERPOINT,
    FileType.ZIP,
    FileType.CSV,
    FileType.OTHERS,
  ],
};
